import {
  Alert,
  Button,
  Group,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { client } from "@/api-client";
import { Header } from "@/components/Header";
import { TrainerRoutes, useNavigator } from "@/routers/routes";

interface FileEditorProps {
  file?: {
    id: string;
    name: string;
    description?: string;
    type: string;
  };
  isCreating?: boolean;
}

export const FileEditor: React.FC<FileEditorProps> = ({
  file,
  isCreating = false,
}) => {
  const { navigate } = useNavigator();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateFile = client.useMutation("post", "/universal/files/{fileId}");

  // Load file data if editing
  useEffect(() => {
    if (file) {
      setName(file.name);
      setDescription(file.description || "");
    }
  }, [file]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!isCreating && file) {
        await updateFile.mutateAsync({
          params: { path: { fileId: file.id } },
          body: { name, description },
        });
      }

      setIsLoading(false);
      navigate({ path: TrainerRoutes.LIBRARY.LIST });
    } catch (error) {
      console.error("Error updating file:", error);
      setError("Failed to save changes. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <Stack>
      <Header title={isCreating ? "Create File" : "Edit File"} />

      <form onSubmit={onSubmit}>
        <Stack>
          {error && (
            <Alert color="red" title="Error" icon={<IconAlertCircle />}>
              {error}
            </Alert>
          )}

          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autosize
            minRows={3}
          />

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => navigate({ path: TrainerRoutes.LIBRARY.LIST })}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              {isCreating ? "Create" : "Save Changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};
